import { functions, auth } from '../firebaseConfig';

// Centralized function to call OpenAI through Firebase Functions (HTTP endpoint)
const callOpenAI = async (action, payload) => {
  // Get the current user's ID token for authentication
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }
  
  const idToken = await user.getIdToken();
  
  // Determine function URL based on environment
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const functionUrl = isLocalhost 
    ? 'http://127.0.0.1:5101/optmizd-bf261/us-central1/callOpenAI'
    : 'https://us-central1-optmizd-bf261.cloudfunctions.net/callOpenAI';
  
  console.log(`🌐 Calling ${isLocalhost ? 'LOCAL' : 'PRODUCTION'} function:`, functionUrl);
  
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({ action, payload })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

export const generateProposal = async (template, projectDescription, selectedExamples, strictAdherence = false, characterMaximum = null, clientName = '') => {
  try {
    console.log(`Generating proposal with ${selectedExamples.length} examples (Strict: ${strictAdherence}):`, selectedExamples.map(e => e.title));
    
    const result = await callOpenAI('generateProposal', {
      template,
      projectDescription,
      selectedExamples,
      strictAdherence,
      characterMaximum,
      clientName
    });

    return result.proposal;
  } catch (error) {
    console.error('Error generating proposal:', error);
    throw error;
  }
};

export const recommendExamples = async (projectDescription, allExamples) => {
  try {
    console.log('Requesting AI recommendations for examples...');
    
    const result = await callOpenAI('recommendExamples', {
      projectDescription,
      allExamples
    });

    const recommendedIndices = result.recommendedIndices;
    console.log('AI recommended example indices:', recommendedIndices);

    const recommendedExamples = recommendedIndices
      .map(idx => allExamples[idx - 1])
      .filter(ex => ex !== undefined);

    console.log('Recommended examples:', recommendedExamples.map(e => e.title));
    return recommendedExamples;
  } catch (error) {
    console.error('Error getting AI recommendations:', error);
    return allExamples.slice(0, 3);
  }
};

export const buildProjectExample = async (projectInfo) => {
  try {
    console.log('Building project example from info...');
    
    const result = await callOpenAI('buildProjectExample', {
      projectInfo
    });

    return result.example;
  } catch (error) {
    console.error('Error building project example:', error);
    throw error;
  }
};

export const answerProposalQuestions = async (questions, proposal, projectDescription, selectedExamples, userProfile = null) => {
  try {
    console.log('Answering proposal questions with AI...');
    
    const result = await callOpenAI('answerProposalQuestions', {
      questions,
      proposal,
      projectDescription,
      selectedExamples,
      userProfile
    });

    return result.answers;
  } catch (error) {
    console.error('Error answering questions:', error);
    throw error;
  }
};

export const regenerateProposal = async (template, projectDescription, selectedExamples, currentProposal, strictAdherence = false) => {
  try {
    console.log('Regenerating proposal...');
    
    const result = await callOpenAI('regenerateProposal', {
      template,
      projectDescription,
      selectedExamples,
      currentProposal,
      strictAdherence
    });

    return result.proposal;
  } catch (error) {
    console.error('Error regenerating proposal:', error);
    throw error;
  }
};

export const scanWebsiteForDescription = async (websiteUrl) => {
  try {
    console.log('Scanning website for description...');
    
    const result = await callOpenAI('scanWebsiteForDescription', {
      websiteUrl
    });

    return result.description;
  } catch (error) {
    console.error('Error scanning website:', error);
    throw error;
  }
};

export const generateTaskList = async (rawInput, template = null, strictAdherence = false, inputMode = 'tasks') => {
  try {
    console.log('Generating task list...');
    
    const result = await callOpenAI('generateTaskList', {
      rawInput,
      template,
      strictAdherence,
      inputMode
    });

    return result.taskList;
  } catch (error) {
    console.error('Error generating task list:', error);
    throw error;
  }
};

export const generateStructuredTasks = async (rawInput, template = null, strictAdherence = false, inputMode = 'tasks', onStream = null) => {
  try {
    console.log('Generating structured tasks...');
    
    // Note: Streaming is not supported through Firebase callable functions
    // If streaming is needed, consider using an HTTP endpoint instead
    
    const result = await callOpenAI('generateStructuredTasks', {
      rawInput,
      template,
      strictAdherence,
      inputMode
    });

    return result.tasks;
  } catch (error) {
    console.error('Error generating structured tasks:', error);
    throw error;
  }
};

export const convertToNotionFormat = (markdown) => {
  // This is a client-side utility function, no API call needed
  const lines = markdown.split('\n');
  const notionBlocks = [];

  lines.forEach(line => {
    if (line.startsWith('# ')) {
      notionBlocks.push({
        type: 'heading_1',
        heading_1: { rich_text: [{ text: { content: line.substring(2) } }] }
      });
    } else if (line.startsWith('## ')) {
      notionBlocks.push({
        type: 'heading_2',
        heading_2: { rich_text: [{ text: { content: line.substring(3) } }] }
      });
    } else if (line.startsWith('- ')) {
      notionBlocks.push({
        type: 'bulleted_list_item',
        bulleted_list_item: { rich_text: [{ text: { content: line.substring(2) } }] }
      });
    } else if (line.trim()) {
      notionBlocks.push({
        type: 'paragraph',
        paragraph: { rich_text: [{ text: { content: line } }] }
      });
    }
  });

  return notionBlocks;
};

export const applySuggestionToTasks = async (tasks, suggestion, rawInput) => {
  try {
    console.log('Applying suggestion to tasks...');
    
    const result = await callOpenAI('applySuggestionToTasks', {
      tasks,
      suggestion,
      rawInput
    });

    return result.tasks;
  } catch (error) {
    console.error('Error applying suggestion:', error);
    throw error;
  }
};

export const generateCaseStudy = async ({
  projectTitle,
  projectDescription,
  challenge,
  solution,
  results,
  technologies,
  timeline,
  teamSize,
  role
}) => {
  try {
    console.log('Generating case study...');
    
    const result = await callOpenAI('generateCaseStudy', {
      projectTitle,
      projectDescription,
      challenge,
      solution,
      results,
      technologies,
      timeline,
      teamSize,
      role
    });

    return result.caseStudy;
  } catch (error) {
    console.error('Error generating case study:', error);
    throw error;
  }
};

export const generateContraVersion = async (proposal, userProfile, selectedExamples) => {
  try {
    console.log('Generating Contra version...');
    
    const result = await callOpenAI('generateContraVersion', {
      proposal,
      userProfile,
      selectedExamples
    });

    return result.contraProposal;
  } catch (error) {
    console.error('Error generating Contra version:', error);
    throw error;
  }
};
